import { useState, useEffect, useMemo } from 'react';
import {
  collection, onSnapshot, query, orderBy,
  addDoc, updateDoc, deleteDoc, doc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  Plus, X, Trash2, HardHat, Save, Users, Building2,
  Search, Filter, Phone, CreditCard, Briefcase,
  ChevronDown, ChevronUp, Edit3, CheckCircle2, AlertCircle,
  UserPlus, ArrowUpDown
} from 'lucide-react';

function kodUret() { return String(Math.floor(100000 + Math.random() * 900000)); }

export default function Calisanlar() {
  const [ustalar, setUstalar] = useState([]);
  const [ekipler, setEkipler] = useState([]);
  const [santiyeler, setSantiyeler] = useState([]);
  const [formAcik, setFormAcik] = useState(false);
  const [ekipFormAcik, setEkipFormAcik] = useState(false);
  const [detayUsta, setDetayUsta] = useState(null);
  const [yeniEkipAd, setYeniEkipAd] = useState('');
  const [form, setForm] = useState({ ad: '', kod: kodUret(), yovmiye: '' });

  // Verimlilik state'leri
  const [arama, setArama] = useState('');
  const [filtreEkip, setFiltreEkip] = useState('');
  const [filtreSantiye, setFiltreSantiye] = useState('');
  const [siralama, setSiralama] = useState('ad'); // 'ad', 'yovmiye', 'kod'
  const [siralamaYon, setSiralamaYon] = useState('asc');
  const [genisletilmisUsta, setGenisletilmisUsta] = useState(null);

  useEffect(() => {
    const unsub1 = onSnapshot(query(collection(db, 'ustalar'), orderBy('olusturmaTarihi', 'desc')), 
      s => setUstalar(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsub2 = onSnapshot(collection(db, 'ekipler'), 
      s => setEkipler(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsub3 = onSnapshot(collection(db, 'santiyeler'), 
      s => setSantiyeler(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => { unsub1(); unsub2(); unsub3(); };
  }, []);

  const ustaEkle = async (e) => {
    e.preventDefault();
    await addDoc(collection(db, 'ustalar'), {
      ...form, yovmiye: Number(form.yovmiye), aktif: true, olusturmaTarihi: serverTimestamp(), santiyeId: ''
    });
    setForm({ ad: '', kod: kodUret(), yovmiye: '' });
    setFormAcik(false);
  };

  const ustaGuncelle = async () => {
    await updateDoc(doc(db, 'ustalar', detayUsta.id), detayUsta);
    setDetayUsta(null);
  };

  const ekipEkle = async () => {
    if(!yeniEkipAd) return;
    await addDoc(collection(db, 'ekipler'), { ad: yeniEkipAd, olusturmaTarihi: serverTimestamp() });
    setYeniEkipAd('');
    setEkipFormAcik(false);
  };

  // İstatistikler
  const istatistikler = useMemo(() => {
    const toplam = ustalar.length;
    const aktifSantiye = new Set(ustalar.filter(u => u.santiyeId).map(u => u.santiyeId)).size;
    const ekipsiz = ustalar.filter(u => !u.ekipId).length;
    const ortYovmiye = toplam > 0 ? Math.round(ustalar.reduce((t, u) => t + (Number(u.yovmiye) || 0), 0) / toplam) : 0;
    return { toplam, aktifSantiye, ekipsiz, ortYovmiye };
  }, [ustalar]);

  // Filtreleme ve sıralama
  const filtrelenmisUstalar = useMemo(() => {
    let sonuc = [...ustalar];

    if (arama.trim()) {
      const q = arama.toLowerCase();
      sonuc = sonuc.filter(u => 
        u.ad?.toLowerCase().includes(q) || 
        u.kod?.includes(q) ||
        u.tel?.includes(q)
      );
    }

    if (filtreEkip) {
      sonuc = sonuc.filter(u => u.ekipId === filtreEkip);
    }

    if (filtreSantiye) {
      sonuc = sonuc.filter(u => u.santiyeId === filtreSantiye);
    }

    sonuc.sort((a, b) => {
      let valA, valB;
      if (siralama === 'ad') { valA = a.ad || ''; valB = b.ad || ''; }
      else if (siralama === 'yovmiye') { valA = Number(a.yovmiye) || 0; valB = Number(b.yovmiye) || 0; }
      else { valA = a.kod || ''; valB = b.kod || ''; }

      if (siralamaYon === 'desc') {
        return valA < valB ? 1 : valA > valB ? -1 : 0;
      }
      return valA > valB ? 1 : valA < valB ? -1 : 0;
    });

    return sonuc;
  }, [ustalar, arama, filtreEkip, filtreSantiye, siralama, siralamaYon]);

  const ekipUyeSayisi = (ekipId) => ustalar.filter(u => u.ekipId === ekipId).length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 pb-24">
      {/* Üst Başlık Barı - Dashboard stili */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white tracking-tight">Ekip & Personel</h1>
          <p className="text-xs text-stone-500 font-bold uppercase tracking-widest mt-1">Saha Görevlendirme ve Atama</p>
        </div>
        <button 
          onClick={() => setFormAcik(true)} 
          className="flex items-center gap-2 bg-white text-black px-5 py-3 rounded-2xl font-black text-xs hover:bg-stone-200 transition-all shadow-lg"
        >
          <UserPlus size={14}/> YENİ PERSONEL
        </button>
      </div>

      {/* İstatistik Kartları - Dashboard StatCard stili */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          label="Toplam Personel" 
          val={istatistikler.toplam} 
          icon={Users} 
          color="text-stone-300" 
          bg="bg-stone-900" 
        />
        <StatCard 
          label="Aktif Şantiye" 
          val={istatistikler.aktifSantiye} 
          icon={Building2} 
          color="text-blue-400" 
          bg="bg-blue-400/5" 
        />
        <StatCard 
          label="Ekipsiz Usta" 
          val={istatistikler.ekipsiz} 
          icon={AlertCircle} 
          color="text-yellow-500" 
          bg="bg-yellow-500/5" 
        />
        <StatCard 
          label="Ort. Yövmiye" 
          val={`₺${istatistikler.ortYovmiye.toLocaleString('tr-TR')}`} 
          icon={Briefcase} 
          color="text-green-400" 
          bg="bg-green-400/5" 
        />
      </div>

      {/* Arama ve Filtre Barı */}
      <div className="bg-stone-900 border border-stone-800 rounded-2xl p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500"/>
            <input 
              type="text"
              placeholder="İsim, kod veya telefon ara..."
              value={arama}
              onChange={e => setArama(e.target.value)}
              className="w-full bg-stone-800 border border-stone-700 rounded-xl pl-9 pr-4 py-3 text-white text-sm outline-none focus:border-stone-500 transition-colors placeholder:text-stone-600"
            />
          </div>
          <div className="flex gap-2">
            <select 
              value={filtreEkip}
              onChange={e => setFiltreEkip(e.target.value)}
              className="bg-stone-800 border border-stone-700 rounded-xl px-3 py-3 text-white text-xs font-bold outline-none focus:border-stone-500"
            >
              <option value="">Tüm Ekipler</option>
              {ekipler.map(e => <option key={e.id} value={e.id}>{e.ad}</option>)}
            </select>
            <select 
              value={filtreSantiye}
              onChange={e => setFiltreSantiye(e.target.value)}
              className="bg-stone-800 border border-stone-700 rounded-xl px-3 py-3 text-white text-xs font-bold outline-none focus:border-stone-500"
            >
              <option value="">Tüm Şantiyeler</option>
              {santiyeler.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
            </select>
            <button 
              onClick={() => {
                setSiralamaYon(siralamaYon === 'asc' ? 'desc' : 'asc');
              }}
              className="bg-stone-800 border border-stone-700 rounded-xl px-3 py-3 text-stone-400 hover:text-white transition-colors"
              title="Sıralama Yönü"
            >
              <ArrowUpDown size={14}/>
            </button>
          </div>
        </div>

        {/* Sıralama Sekmeleri */}
        <div className="flex gap-1">
          {[
            { key: 'ad', label: 'İsim' },
            { key: 'yovmiye', label: 'Yövmiye' },
            { key: 'kod', label: 'Kod' }
          ].map(s => (
            <button
              key={s.key}
              onClick={() => {
                if (siralama === s.key) {
                  setSiralamaYon(siralamaYon === 'asc' ? 'desc' : 'asc');
                } else {
                  setSiralama(s.key);
                  setSiralamaYon('asc');
                }
              }}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${
                siralama === s.key 
                  ? 'bg-white text-black' 
                  : 'bg-stone-800 text-stone-500 hover:text-stone-300'
              }`}
            >
              {s.label} {siralama === s.key && (siralamaYon === 'asc' ? '↑' : '↓')}
            </button>
          ))}
          {(arama || filtreEkip || filtreSantiye) && (
            <button
              onClick={() => { setArama(''); setFiltreEkip(''); setFiltreSantiye(''); }}
              className="ml-auto px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
            >
              Filtreleri Temizle
            </button>
          )}
        </div>
      </div>

      {/* Ana Layout: Ekipler + Usta Listesi */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sol Panel: Ekipler */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-stone-900 border border-stone-800 rounded-3xl p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2 text-stone-400">
                <Users size={14}/>
                <h2 className="text-xs font-bold uppercase tracking-widest">Ekipler</h2>
              </div>
              <button 
                onClick={() => setEkipFormAcik(true)} 
                className="p-2 bg-stone-800 rounded-xl text-white hover:bg-stone-700 transition-colors"
              >
                <Plus size={14}/>
              </button>
            </div>

            <div className="space-y-2">
              {/* "Tümü" seçeneği */}
              <button
                onClick={() => setFiltreEkip('')}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                  filtreEkip === '' 
                    ? 'bg-white text-black' 
                    : 'bg-stone-800/40 border border-stone-700/50 text-stone-300 hover:bg-stone-800'
                }`}
              >
                <span className="text-sm font-bold">Tüm Personel</span>
                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                  filtreEkip === '' ? 'bg-black/20 text-black' : 'bg-stone-700 text-stone-400'
                }`}>
                  {ustalar.length}
                </span>
              </button>

              {ekipler.map(e => (
                <button
                  key={e.id}
                  onClick={() => setFiltreEkip(filtreEkip === e.id ? '' : e.id)}
                  className={`w-full flex items-center justify-between p-3 rounded-xl transition-all group ${
                    filtreEkip === e.id 
                      ? 'bg-white text-black' 
                      : 'bg-stone-800/40 border border-stone-700/50 text-stone-300 hover:bg-stone-800'
                  }`}
                >
                  <span className="text-sm font-bold truncate">{e.ad}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                      filtreEkip === e.id ? 'bg-black/20 text-black' : 'bg-stone-700 text-stone-400'
                    }`}>
                      {ekipUyeSayisi(e.id)}
                    </span>
                    <button 
                      onClick={(ev) => { ev.stopPropagation(); deleteDoc(doc(db, 'ekipler', e.id)); }}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/10 text-red-500 rounded-lg transition-all"
                    >
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Şantiye Dağılımı Mini Panel */}
          <div className="bg-stone-900 border border-stone-800 rounded-3xl p-6 shadow-2xl">
            <div className="flex items-center gap-2 mb-4 text-stone-400">
              <Building2 size={14}/>
              <h2 className="text-xs font-bold uppercase tracking-widest">Şantiye Dağılımı</h2>
            </div>
            <div className="space-y-2">
              {santiyeler.map(s => {
                const sayi = ustalar.filter(u => u.santiyeId === s.id).length;
                if (sayi === 0) return null;
                return (
                  <button
                    key={s.id}
                    onClick={() => setFiltreSantiye(filtreSantiye === s.id ? '' : s.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                      filtreSantiye === s.id 
                        ? 'bg-blue-500/10 border border-blue-500/30 text-blue-300' 
                        : 'bg-stone-800/40 border border-stone-700/50 text-stone-300 hover:bg-stone-800'
                    }`}
                  >
                    <span className="text-sm font-bold truncate">{s.ad}</span>
                    <span className="text-[10px] font-black bg-stone-700 text-stone-400 px-2 py-0.5 rounded-full">
                      {sayi} kişi
                    </span>
                  </button>
                );
              })}
              {ustalar.filter(u => !u.santiyeId).length > 0 && (
                <button
                  onClick={() => setFiltreSantiye('bos')}
                  className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                    filtreSantiye === 'bos' 
                      ? 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-300' 
                      : 'bg-stone-800/40 border border-stone-700/50 text-stone-500 hover:bg-stone-800'
                  }`}
                >
                  <span className="text-sm font-bold">Atama Bekliyor</span>
                  <span className="text-[10px] font-black bg-stone-700 text-stone-400 px-2 py-0.5 rounded-full">
                    {ustalar.filter(u => !u.santiyeId).length}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Sağ Panel: Usta Listesi */}
        <div className="lg:col-span-3">
          <div className="bg-stone-900 border border-stone-800 rounded-3xl overflow-hidden shadow-2xl">
            <div className="px-6 py-4 border-b border-stone-800 bg-stone-900/50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <HardHat size={16} className="text-stone-500"/>
                <h2 className="text-xs font-bold text-white uppercase tracking-widest">
                  Personel Listesi 
                  <span className="text-stone-500 ml-2">({filtrelenmisUstalar.length} sonuç)</span>
                </h2>
              </div>
            </div>

            <div className="divide-y divide-stone-800/50">
              {filtrelenmisUstalar.length === 0 ? (
                <div className="p-12 text-center">
                  <Users size={32} className="mx-auto text-stone-700 mb-3"/>
                  <p className="text-stone-500 text-sm font-bold">Personel bulunamadı</p>
                  <p className="text-stone-600 text-xs mt-1">Filtreleri değiştirmeyi veya yeni personel eklemeyi deneyin</p>
                </div>
              ) : (
                filtrelenmisUstalar.map(u => {
                  const ekip = ekipler.find(e => e.id === u.ekipId);
                  const santiye = santiyeler.find(s => s.id === u.santiyeId);
                  const genisletildi = genisletilmisUsta === u.id;

                  return (
                    <div 
                      key={u.id} 
                      className="hover:bg-stone-800/20 transition-all"
                    >
                      <div 
                        onClick={() => setGenisletilmisUsta(genisletildi ? null : u.id)}
                        className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer"
                      >
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="w-12 h-12 rounded-2xl bg-stone-800 border border-stone-700 flex items-center justify-center text-stone-500 font-black text-lg shrink-0">
                            {u.ad?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-bold text-white truncate">{u.ad}</h3>
                              {u.aktif === false && (
                                <span className="text-[9px] font-black bg-red-500/10 text-red-400 px-1.5 py-0.5 rounded border border-red-500/20">
                                  PASİF
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {ekip && (
                                <span className="text-[9px] font-bold bg-stone-800 text-stone-400 px-1.5 py-0.5 rounded border border-stone-700/30">
                                  {ekip.ad}
                                </span>
                              )}
                              {santiye && (
                                <span className="text-[9px] font-bold bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded border border-blue-500/20">
                                  <Building2 size={8} className="inline mr-0.5"/>{santiye.ad}
                                </span>
                              )}
                              {!santiye && (
                                <span className="text-[9px] font-bold bg-yellow-500/10 text-yellow-500 px-1.5 py-0.5 rounded border border-yellow-500/20">
                                  ATAMA BEKLİYOR
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 sm:flex items-center gap-4 sm:gap-8 border-t border-stone-800 sm:border-0 pt-4 sm:pt-0">
                          <DataBox label="Kod" val={`#${u.kod}`} />
                          <DataBox 
                            label="Yövmiye" 
                            val={`₺${Number(u.yovmiye || 0).toLocaleString('tr-TR')}`} 
                            color="text-green-400" 
                          />
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); setDetayUsta(u); }}
                              className="p-2 bg-stone-800 rounded-xl text-stone-400 hover:text-white hover:bg-stone-700 transition-colors"
                            >
                              <Edit3 size={14}/>
                            </button>
                            {genisletildi ? <ChevronUp size={14} className="text-stone-500"/> : <ChevronDown size={14} className="text-stone-500"/>}
                          </div>
                        </div>
                      </div>

                      {/* Genişletilmiş Detaylar */}
                      {genisletildi && (
                        <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
                          <div className="bg-stone-800/30 border border-stone-700/30 rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-stone-800 flex items-center justify-center text-stone-500">
                                <Phone size={12}/>
                              </div>
                              <div>
                                <div className="text-[9px] text-stone-600 uppercase font-black">Telefon</div>
                                <div className="text-xs font-bold text-stone-300">{u.tel || '-'}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-stone-800 flex items-center justify-center text-stone-500">
                                <CreditCard size={12}/>
                              </div>
                              <div>
                                <div className="text-[9px] text-stone-600 uppercase font-black">IBAN</div>
                                <div className="text-xs font-bold text-stone-300 font-mono truncate">{u.iban || '-'}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-stone-800 flex items-center justify-center text-stone-500">
                                <CheckCircle2 size={12}/>
                              </div>
                              <div>
                                <div className="text-[9px] text-stone-600 uppercase font-black">Durum</div>
                                <div className="text-xs font-bold text-stone-300">{u.aktif !== false ? 'Aktif' : 'Pasif'}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* USTA DETAY MODAL */}
      {detayUsta && (
        <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-stone-900 border border-stone-800 w-full max-w-lg rounded-[2.5rem] p-8 space-y-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-xl font-black text-white">Usta Bilgileri</h3>
                <p className="text-xs text-stone-500 font-bold uppercase tracking-wider mt-1">#{detayUsta.kod}</p>
              </div>
              <button 
                onClick={() => setDetayUsta(null)} 
                className="p-2 bg-stone-800 rounded-full text-stone-400 hover:text-white transition-colors"
              >
                <X size={18}/>
              </button>
            </div>

            <div className="space-y-4">
              {/* Ad & Yövmiye */}
              <div>
                <label className="text-[10px] font-black text-stone-500 uppercase mb-1.5 block">Ad Soyad / Yövmiye</label>
                <div className="flex gap-2">
                  <input 
                    className="flex-1 bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm outline-none focus:border-stone-500 transition-colors" 
                    value={detayUsta.ad || ''} 
                    onChange={e => setDetayUsta({...detayUsta, ad: e.target.value})} 
                    placeholder="Ad Soyad"
                  />
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-xs font-bold">₺</span>
                    <input 
                      type="number" 
                      className="w-28 bg-stone-800 border border-stone-700 rounded-xl p-3 pl-7 text-white text-sm outline-none focus:border-stone-500 transition-colors" 
                      value={detayUsta.yovmiye || ''} 
                      onChange={e => setDetayUsta({...detayUsta, yovmiye: e.target.value})} 
                      placeholder="Yövmiye"
                    />
                  </div>
                </div>
              </div>

              {/* Şantiye */}
              <div>
                <label className="text-[10px] font-black text-stone-500 uppercase mb-1.5 block">Şantiye Görevlendirmesi</label>
                <select 
                  className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm outline-none focus:border-stone-500 transition-colors" 
                  value={detayUsta.santiyeId || ''} 
                  onChange={e => setDetayUsta({...detayUsta, santiyeId: e.target.value})}
                >
                  <option value="">Şantiye Atanmamış</option>
                  {santiyeler.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
                </select>
              </div>

              {/* Ekip & Telefon */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black text-stone-500 uppercase mb-1.5 block">Ekip Ataması</label>
                  <select 
                    className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm outline-none focus:border-stone-500 transition-colors" 
                    value={detayUsta.ekipId || ''} 
                    onChange={e => setDetayUsta({...detayUsta, ekipId: e.target.value})}
                  >
                    <option value="">Ekipsiz</option>
                    {ekipler.map(e => <option key={e.id} value={e.id}>{e.ad}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-black text-stone-500 uppercase mb-1.5 block">Telefon</label>
                  <input 
                    className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm outline-none focus:border-stone-500 transition-colors" 
                    value={detayUsta.tel || ''} 
                    onChange={e => setDetayUsta({...detayUsta, tel: e.target.value})} 
                    placeholder="05XX XXX XX XX"
                  />
                </div>
              </div>

              {/* IBAN */}
              <div>
                <label className="text-[10px] font-black text-stone-500 uppercase mb-1.5 block">IBAN</label>
                <input 
                  className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-mono outline-none focus:border-stone-500 transition-colors" 
                  value={detayUsta.iban || ''} 
                  onChange={e => setDetayUsta({...detayUsta, iban: e.target.value})} 
                  placeholder="TR00 0000 0000 0000 0000 0000 00"
                />
              </div>

              {/* Aktif/Pasif Toggle */}
              <div className="flex items-center justify-between bg-stone-800/30 border border-stone-700/30 rounded-xl p-3">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${detayUsta.aktif !== false ? 'bg-green-400' : 'bg-red-400'}`}/>
                  <span className="text-xs font-bold text-stone-300">
                    Durum: {detayUsta.aktif !== false ? 'Aktif' : 'Pasif'}
                  </span>
                </div>
                <button
                  onClick={() => setDetayUsta({...detayUsta, aktif: detayUsta.aktif === false ? true : false})}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all ${
                    detayUsta.aktif !== false 
                      ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' 
                      : 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                  }`}
                >
                  {detayUsta.aktif !== false ? 'PASİF YAP' : 'AKTİF YAP'}
                </button>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button 
                onClick={() => {
                  if (confirm('Bu ustayı silmek istediğinize emin misiniz?')) {
                    deleteDoc(doc(db, 'ustalar', detayUsta.id)).then(() => setDetayUsta(null));
                  }
                }} 
                className="flex-1 py-4 bg-red-500/10 text-red-500 rounded-2xl font-bold text-xs hover:bg-red-500/20 transition-colors"
              >
                SİL
              </button>
              <button 
                onClick={ustaGuncelle} 
                className="flex-[2] py-4 bg-white text-black rounded-2xl font-black text-xs flex items-center justify-center gap-2 hover:bg-stone-200 transition-colors"
              >
                <Save size={16}/> KAYDET
              </button>
            </div>
          </div>
        </div>
      )}

      {/* YENİ USTA EKLEME MODAL */}
      {formAcik && (
        <div className="fixed inset-0 z-[110] bg-black/95 backdrop-blur-sm flex items-center justify-center p-6">
          <form onSubmit={ustaEkle} className="bg-stone-900 p-8 rounded-[2.5rem] w-full max-w-md border border-stone-800 space-y-5 shadow-2xl">
            <div className="text-center mb-2">
              <div className="w-12 h-12 bg-stone-800 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <UserPlus size={20} className="text-stone-400"/>
              </div>
              <h4 className="text-white font-black uppercase text-lg">Yeni Usta Kaydı</h4>
              <p className="text-xs text-stone-500 font-bold uppercase tracking-wider mt-1">Otomatik kod: #{form.kod}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-black text-stone-500 uppercase mb-1.5 block">Ad Soyad</label>
                <input 
                  className="w-full bg-stone-800 border border-stone-700 p-4 rounded-xl text-white outline-none focus:border-stone-500 transition-colors placeholder:text-stone-600" 
                  placeholder="Örn: Ahmet Yılmaz" 
                  value={form.ad} 
                  onChange={e => setForm({...form, ad: e.target.value})} 
                  required 
                  autoFocus
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-stone-500 uppercase mb-1.5 block">Günlük Yövmiye</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500 text-sm font-bold">₺</span>
                  <input 
                    type="number" 
                    className="w-full bg-stone-800 border border-stone-700 p-4 pl-8 rounded-xl text-white outline-none focus:border-stone-500 transition-colors placeholder:text-stone-600" 
                    placeholder="Örn: 850" 
                    value={form.yovmiye} 
                    onChange={e => setForm({...form, yovmiye: e.target.value})} 
                    required 
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button 
                type="button" 
                onClick={() => setFormAcik(false)} 
                className="flex-1 py-4 text-stone-500 text-xs font-bold hover:text-stone-300 transition-colors"
              >
                İPTAL
              </button>
              <button 
                type="submit" 
                className="flex-1 py-4 bg-white text-black rounded-2xl text-xs font-black hover:bg-stone-200 transition-colors"
              >
                KAYDET
              </button>
            </div>
          </form>
        </div>
      )}

      {/* YENİ EKİP MODAL */}
      {ekipFormAcik && (
        <div className="fixed inset-0 z-[110] bg-black/95 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="bg-stone-900 p-8 rounded-[2.5rem] w-full max-w-sm border border-stone-800 shadow-2xl">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-stone-800 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Users size={20} className="text-stone-400"/>
              </div>
              <h4 className="text-white font-black text-lg">Yeni Ekip Oluştur</h4>
            </div>

            <div className="mb-6">
              <label className="text-[10px] font-black text-stone-500 uppercase mb-1.5 block">Ekip Adı</label>
              <input 
                autoFocus 
                className="w-full bg-stone-800 border border-stone-700 p-4 rounded-xl text-white outline-none focus:border-stone-500 transition-colors placeholder:text-stone-600" 
                placeholder="Örn: Dış Cephe Ekibi" 
                value={yeniEkipAd} 
                onChange={e => setYeniEkipAd(e.target.value)} 
              />
            </div>

            <div className="flex gap-3">
              <button 
                onClick={() => setEkipFormAcik(false)} 
                className="flex-1 py-3 text-stone-500 text-xs font-bold hover:text-stone-300 transition-colors"
              >
                İPTAL
              </button>
              <button 
                onClick={ekipEkle} 
                className="flex-1 py-3 bg-white text-black rounded-2xl text-xs font-black hover:bg-stone-200 transition-colors"
              >
                OLUŞTUR
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Yardımcı Bileşenler - Dashboard ile birebir uyumlu */

function StatCard({ label, val, icon: Icon, color, bg }) {
  return (
    <div className={`${bg} border border-stone-800 p-5 rounded-3xl shadow-lg`}>
      <div className="flex items-center gap-2 mb-3 text-stone-500">
        <Icon size={14}/> 
        <span className="text-[10px] font-black uppercase tracking-wider">{label}</span>
      </div>
      <div className={`text-xl font-black tracking-tight ${color}`}>{val}</div>
    </div>
  );
}

function DataBox({ label, val, color="text-stone-300", bold=false }) {
  return (
    <div className="text-center sm:text-right">
      <div className="text-[9px] text-stone-600 uppercase font-black mb-0.5">{label}</div>
      <div className={`text-xs ${bold ? 'font-black' : 'font-bold'} ${color} truncate`}>{val}</div>
    </div>
  );
}